import { S25Date } from "./s25.date";
import { S25DateParse } from "./s25.date.parse";
import { isDate } from "./index";

export namespace S25Timezone {
    export function convert(date: any, tzName: string, format?: string): Date {
        // NOTE: THIS METHOD DOES NOT CONVERT DATES TO A GIVEN TIMEZONE
        // If you need to convert a date use the "convertTo()" method below, which does work.
        // For whatever reason the timezone argument is not used, and this method ends up just cloning the date

        // note: Date is assummed in browser's local timezone
        // note: if no tzName (i.e. no userprefs - return cloned date 'as is', so browser's default timezone will be used
        const d: any = window.dayjs(date);

        // note: intentionally strip offset details and re-create JS Date() with current browser's offset
        const formattedDtStr: string = d.format(format || _STARTOF_FORMAT.millisecond);

        // parse as Date
        return S25DateParse(formattedDtStr);
    }

    export function initDayjs(): void {
        if (!window.dayjs.tz) {
            window.dayjs.extend(window.dayjs_plugin_utc);
            window.dayjs.extend(window.dayjs_plugin_timezone);
        }
    }

    export function representation(
        date: Date,
        timezone: string,
    ): {
        year: number;
        month: number;
        day: number;
        hour: number;
        minute: number;
        second: number;
        millisecond: number;
    } {
        initDayjs();
        const d = window.dayjs.tz(date, timezone);
        return {
            year: d.year(),
            month: d.month(),
            day: d.day(),
            hour: d.hour(),
            minute: d.minute(),
            second: d.second(),
            millisecond: d.millisecond(),
        };
    }

    // Parses a date in the given timezone
    // If date is a JS Date object, then the timezone is discarded and the date and time is parsed in the given timezone
    export function parse(date: string | Date, timezone: string): Date {
        initDayjs();
        if (isDate(date)) date = S25Date.dropTZString(date);
        return window.dayjs.tz(date, timezone).toDate();
    }

    // Moves a date to specific time in the specified timezone
    export function toTime(date: Date, timezone: string, hour?: number, min?: number, sec?: number, ms?: number): Date {
        initDayjs();
        let d = window.dayjs(date).tz(timezone);
        if (ms !== undefined) d = d.millisecond(ms);
        if (sec !== undefined) d = d.second(sec);
        if (min !== undefined) d = d.minute(min);
        if (hour !== undefined) d = d.hour(hour);
        return d.toDate();
    }

    // Keep date and time, but convert to a different timezone
    export function convertTo(date: Date, timezone: string): Date {
        initDayjs();
        return window.dayjs(date).tz(timezone, true).toDate();
    }

    export function currentYear(tzName: string): Date {
        return _current(tzName, "year");
    }

    export function currentMonth(tzName: string): Date {
        return _current(tzName, "month");
    }

    export function currentDay(tzName: string): Date {
        return _current(tzName, "day");
    }

    export function currentHour(tzName: string): Date {
        return _current(tzName, "hour");
    }

    export function currentSecond(tzName: string): Date {
        return _current(tzName, "second");
    }

    export function nextHour(tzName: string): Date {
        return S25Date.addHours(currentHour(tzName), +1);
    }

    export function _current(
        tzName: string,
        startOf: "year" | "month" | "day" | "hour" | "minute" | "second" | "millisecond",
    ): Date {
        return convert(S25Date.current(), tzName, _STARTOF_FORMAT[startOf]);
    }

    export const _STARTOF_FORMAT = {
        year: "YYYY",
        month: "MM",
        day: "YYYY-MM-DD",
        hour: "YYYY-MM-DD HH",
        minute: "YYYY-MM-DD HH:mm",
        second: "YYYY-MM-DD HH:mm:ss",
        millisecond: "YYYY-MM-DD HH:mm:ss.SSS",
    } as const;

    export function getCurrentTime(timezone: string): {
        year: number;
        month: number;
        day: number;
        hour: number;
        minute: number;
        second: number;
        millisecond: number;
    } {
        return representation(new Date(), timezone);
    }

    // Return the number of MS that have passed today
    export function getDayProgress(timezone: string): number {
        const { hour, minute, second, millisecond } = getCurrentTime(timezone);
        return hour * 3_600_000 + minute * 60_000 + second * 1000 + millisecond;
    }
}
